import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Twitter, Github, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { company, navLinks, services } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";

export default function Footer() {
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const submit = (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const list = JSON.parse(localStorage.getItem("bw_newsletter") || "[]");
      list.push({ email, at: new Date().toISOString() });
      localStorage.setItem("bw_newsletter", JSON.stringify(list));
    } catch { /* ignore storage errors */ }
    toast({ title: "You're on the list", description: `We'll email engineering notes to ${email}.` });
    setEmail("");
  };

  return (
    <footer className="relative mt-24 bg-slate-950 text-slate-300 overflow-hidden">
      <div className="absolute inset-0 circuit-lines opacity-30" />
      <div className="absolute -top-24 -right-24 w-[32rem] h-[32rem] rounded-full bg-brand-500/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Logo size={72} />
            <p className="mt-5 text-[15px] leading-relaxed text-slate-400 max-w-sm">
              {company.description}
            </p>

            <form onSubmit={submit} className="mt-6 flex gap-2 max-w-sm">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@work-email.com"
                className="bg-slate-900 border-slate-800 text-white placeholder:text-slate-500 h-11 rounded-full px-5 focus-visible:ring-brand-500"
              />
              <Button type="submit" className="h-11 rounded-full bg-brand-500 hover:bg-brand-400 text-slate-950 font-semibold px-5">
                Subscribe <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </form>
          </div>

          <div className="lg:col-span-2">
            <div className="text-white font-semibold text-sm mb-4">Company</div>
            <ul className="space-y-2.5 text-[14px]">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-brand-300 transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-white font-semibold text-sm mb-4">Services</div>
            <ul className="space-y-2.5 text-[14px]">
              {services.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <Link to={`/services#${s.id}`} className="hover:text-brand-300 transition-colors">{s.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-white font-semibold text-sm mb-4">Get in touch</div>
            <ul className="space-y-3 text-[14px]">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                <span>{company.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                <a href={`tel:${company.phone}`} className="hover:text-brand-300">{company.phone}</a>
              </li>
              <li className="flex gap-3">
                <Mail className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                <a href={`mailto:${company.email}`} className="hover:text-brand-300">{company.email}</a>
              </li>
            </ul>

            <div className="flex items-center gap-2 mt-5">
              {[Linkedin, Twitter, Github].map((I, i) => (
                <a key={i} href="#" className="h-9 w-9 rounded-full border border-slate-800 grid place-items-center hover:border-brand-500 hover:text-brand-300 transition-colors" aria-label="social">
                  <I className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[13px] text-slate-500">
          <div>© {new Date().getFullYear()} {company.name}. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <a href="#" className="hover:text-brand-300">Privacy</a>
            <a href="#" className="hover:text-brand-300">Terms</a>
            <a href="#" className="hover:text-brand-300">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
